if (!customElements.get('style-quiz')) {
  class StyleQuiz extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0; // 0 = not started, 1-5 = questions, 6 = result
      this.answers = new Array(5).fill(null);
      this.questions = [];
      this.translations = {};
      this.resultsData = {};
    }

    connectedCallback() {
      // Parse embedded JSON data
      this.data = this.loadData();
      this.questions = this.data.questions || [];
      this.translations = this.data.translations || {};
      this.resultsData = this.data.results || {};

      // Cache DOM elements
      this.bottomSheet = this.querySelector('.style-quiz-bottom-sheet');
      this.fullscreen = this.querySelector('.style-quiz-fullscreen');
      this.progressText = this.querySelector('.quiz-progress__text');
      this.progressFill = this.querySelector('.quiz-progress__fill');
      this.questionScreen = this.querySelector('.quiz-question');
      this.resultScreen = this.querySelector('.quiz-result');

      // Check cookie — if already completed, do nothing
      if (getCookie('style-quiz-completed')) return;

      // Bind event handlers
      this.bindEvents();

      // Design mode — show bottom sheet immediately
      if (this.dataset.designMode === 'true') {
        this.showBottomSheet();
        return;
      }

      // Set up scroll trigger
      this.setupTrigger();
    }

    loadData() {
      const scriptEl = this.querySelector('script[type="application/json"]');
      if (!scriptEl) return {};
      try {
        return JSON.parse(scriptEl.textContent);
      } catch (e) {
        console.error('Style Quiz: Failed to parse data', e);
        return {};
      }
    }

    bindEvents() {
      // Click on overlay to dismiss bottom sheet
      this.bottomSheet.addEventListener('click', (e) => {
        if (e.target === this.bottomSheet) {
          this.dismiss();
        }
      });

      this.addEventListener('click', (e) => {
        const target = e.target.closest('[data-quiz-action]');
        if (!target) return;

        const action = target.dataset.quizAction;

        switch (action) {
          case 'start':
            this.startQuiz();
            break;
          case 'dismiss':
            this.dismiss();
            break;
          case 'close':
            this.closeFullscreen();
            break;
          case 'select':
            this.selectOption(parseInt(target.dataset.optionIndex, 10));
            break;
          case 'back':
            this.goBack();
            break;
          case 'retake':
            this.retake();
            break;
        }
      });
    }

    setupTrigger() {
      this.triggerTimeout = setTimeout(() => {
        this.showBottomSheet();
      }, 30_000); // 30 seconds
    }

    // --- Bottom Sheet ---

    showBottomSheet() {
      this.bottomSheet.classList.add('is-visible');
    }

    hideBottomSheet() {
      this.bottomSheet.classList.remove('is-visible');
    }

    dismiss() {
      this.hideBottomSheet();
      this.setCookieAndStop();
      document.dispatchEvent(new CustomEvent('style-quiz:dismissed'));
    }

    // --- Fullscreen Quiz ---

    startQuiz() {
      this.hideBottomSheet();
      this.currentStep = 1;
      this.answers.fill(null);
      this.fullscreen.classList.add('is-active');
      if (typeof bodyScroll !== 'undefined') {
        bodyScroll.lock(this.fullscreen);
      }
      this.renderQuestion();
    }

    closeFullscreen() {
      this.fullscreen.classList.remove('is-active');
      if (typeof bodyScroll !== 'undefined') {
        bodyScroll.unlock(this.fullscreen);
      }
      this.setCookieAndStop();
      document.dispatchEvent(new CustomEvent('style-quiz:dismissed'));
    }

    // --- Question Navigation ---

    selectOption(optionIndex) {
      this.answers[this.currentStep - 1] = optionIndex;

      // Highlight selected option
      const options = this.questionScreen.querySelectorAll('.quiz-option');
      options.forEach((opt, i) => {
        opt.classList.toggle('is-selected', i === optionIndex);
      });

      // Auto-advance after brief delay
      setTimeout(() => {
        if (this.currentStep < this.questions.length) {
          this.currentStep++;
          this.renderQuestion();
        } else {
          this.showResult();
        }
      }, 300);
    }

    goBack() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.renderQuestion();
      } else {
        // On Q1, close everything
        this.closeFullscreen();
      }
    }

    retake() {
      this.currentStep = 1;
      this.answers.fill(null);
      this.renderQuestion();
    }

    // --- Rendering ---

    renderQuestion() {
      const q = this.questions[this.currentStep - 1];
      if (!q) return;

      const progressOf = this.translations.progress_of || 'of';

      // Update progress
      if (this.progressText) {
        this.progressText.textContent = `${this.currentStep} ${progressOf} ${this.questions.length}`;
      }
      if (this.progressFill) {
        this.progressFill.style.width = `${(this.currentStep / this.questions.length) * 100}%`;
      }

      // Update question text
      const questionText = this.questionScreen.querySelector('.quiz-question__text');
      if (questionText) questionText.textContent = q.text;

      // Update options
      const optionsContainer = this.questionScreen.querySelector('.quiz-options');
      if (optionsContainer) {
        const selectedAnswer = this.answers[this.currentStep - 1];
        optionsContainer.innerHTML = q.options
          .map((option, i) => {
            const selectedClass = selectedAnswer === i ? ' is-selected' : '';
            return `<button type="button" class="quiz-option${selectedClass}" data-quiz-action="select" data-option-index="${i}">${this.escapeHtml(option)}</button>`;
          })
          .join('');
      }

      // Show question screen, hide result
      this.questionScreen.classList.add('is-active');
      this.resultScreen.classList.remove('is-active');

      // Show progress bar
      this.querySelector('.quiz-progress').style.display = '';
    }

    showResult() {
      const resultKey = this.calculateResult();
      let result = this.resultsData[resultKey];

      // Fallback: use the first available result if the calculated key isn't configured
      if (!result) {
        const firstKey = Object.keys(this.resultsData)[0];
        result = firstKey ? this.resultsData[firstKey] : null;
      }

      if (!result) {
        console.error('Style Quiz: no result data found. Ensure all result blocks are configured in the theme editor.');
        return;
      }

      // Populate result screen
      const titleEl = this.resultScreen.querySelector('.quiz-result__title');
      const descEl = this.resultScreen.querySelector('.quiz-result__description');
      const imageContainer = this.resultScreen.querySelector('.quiz-result__image');
      const imageEl = imageContainer ? imageContainer.querySelector('img') : null;
      const ctaEl = this.resultScreen.querySelector('.quiz-result__cta');

      if (titleEl) titleEl.textContent = result.title || '';
      if (descEl) descEl.innerHTML = result.description || '';

      if (imageEl && imageContainer) {
        if (result.image) {
          imageEl.src = result.image;
          imageEl.alt = result.title || '';
          imageContainer.style.display = '';
        } else {
          imageContainer.style.display = 'none';
        }
      }

      if (ctaEl) {
        if (result.page_url) {
          ctaEl.href = result.page_url;
          ctaEl.textContent = this.translations.result_cta || 'Discover Your Style';
          ctaEl.style.display = '';
        } else {
          ctaEl.style.display = 'none';
        }
      }

      // Switch screens
      this.questionScreen.classList.remove('is-active');
      this.resultScreen.classList.add('is-active');

      // Hide progress bar
      this.querySelector('.quiz-progress').style.display = 'none';

      // Set cookie
      setCookie('style-quiz-completed', 'true', 365);
    }

    calculateResult() {
      const keys = ['solea', 'forma', 'ligne', 'noire', 'fluide'];
      const scores = [0, 0, 0, 0, 0];

      this.answers.forEach((answer) => {
        if (answer !== null && answer >= 0 && answer < keys.length) {
          scores[answer]++;
        }
      });

      const maxScore = Math.max(...scores);
      return keys[scores.indexOf(maxScore)];
    }

    // --- Utilities ---

    setCookieAndStop() {
      setCookie('style-quiz-completed', 'true', 365);
      if (this.triggerTimeout) {
        clearTimeout(this.triggerTimeout);
      }
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('style-quiz', StyleQuiz);
}
